// ============================================================================
// consultantSnapshotService.js
//
// Updated: SNOW branches now call snApi (injected via setSNApiForSnapshots)
// instead of returning empty stubs.
// ============================================================================

let forgeModule = null;
const loadForge = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const invoke = async (fn, payload) => {
  const { invoke: forgeInvoke } = await loadForge();
  return forgeInvoke(fn, payload);
};

// Product edition is separate from the legacy pricing plan. Snapshot operations
// must not invoke Forge resolvers in the dedicated Jira Free application.
const isJiraFreeEdition = () =>
  typeof window !== 'undefined' && window.__METRICUS_JIRA_PRODUCT_EDITION__ === 'free';

// ── SNOW API injection ───────────────────────────────────────────────────────
let _snApi = null;
export function setSNApiForSnapshots(snApi) { _snApi = snApi; }

// Web uses the same cookie-authenticated API as the Process Advisor web client.
const webSnapshotApi = async (modelId, suffix = '', options = {}) => {
  const base = window.location.hostname === 'advisor.metricus.com'
    ? 'https://api.advisor.metricus.com/api' : '/api';
  const response = await fetch(`${base}/models/${encodeURIComponent(modelId)}/advisor-snapshots${suffix}`, {
    credentials: 'include',
    ...options,
    headers: {...(options.body ? {'Content-Type':'application/json'} : {}), ...(options.headers||{})},
  });
  const result = await response.json().catch(()=>({}));
  if(!response.ok) throw new Error(result.error || `Snapshot request failed (${response.status})`);
  return result;
};

export const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });

export const saveSnapshot = async ({ modelId, tab, tabLabel, description, html, AppVersion }) => {
  if (isJiraFreeEdition()) return { success: false, error: "Snapshots require Enterprise" };
  if (!modelId || !tab || !html) return { success: false };
  const id = uuid();
  const createdAt = Date.now();

  if (AppVersion === 'FORGE') {
    return invoke('saveConsultantSnapshot', { id, modelId, tab, tabLabel, description: description || null, html, createdAt });
  }
  if (AppVersion === 'SNOW') {
    if (_snApi && typeof _snApi.saveSnapshot === 'function') {
      return _snApi.saveSnapshot({ id, modelId, tab, tabLabel, description: description || null, html, createdAt });
    }
    console.warn('saveSnapshot : snApi not injected');
    return { success: false };
  }
  if (AppVersion === 'WEB') {
    return webSnapshotApi(modelId, '', {method:'POST', body:JSON.stringify({tab,tabLabel,description:description||null,html})});
  }
  try {
    const res = await fetch(`https://process-optimizer-jira.metricus.com/api/processmodel/${modelId}/consultant-snapshot`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id, tab, tabLabel, description: description || null, html, createdAt }),
    });
    return { success: true, id, ...await res.json() };
  } catch (err) { console.error('saveSnapshot [Direct] failed:', err); return { success: false }; }
};

export const listSnapshots = async ({ modelId, AppVersion }) => {
  if (isJiraFreeEdition()) return { snapshots: [] };
  if (!modelId) return { snapshots: [] };
  if (AppVersion === 'FORGE') return invoke('listConsultantSnapshots', { modelId });
  if (AppVersion === 'SNOW') {
    if (_snApi && typeof _snApi.listSnapshots === 'function') {
      return _snApi.listSnapshots(modelId);
    }
    return { snapshots: [] };
  }
  if (AppVersion === 'WEB') {
    return webSnapshotApi(modelId);
  }
  try {
    const res = await fetch(`https://process-optimizer-jira.metricus.com/api/processmodel/${modelId}/consultant-snapshots`);
    return await res.json();
  } catch (err) { console.error('listSnapshots [Direct] failed:', err); return { snapshots: [] }; }
};

export const getSnapshot = async ({ id, modelId, AppVersion }) => {
  if (isJiraFreeEdition()) return { snapshot: null };
  if (AppVersion === 'FORGE') return invoke('getConsultantSnapshot', { id });
  if (AppVersion === 'SNOW') {
    if (_snApi && typeof _snApi.getSnapshot === 'function') {
      return _snApi.getSnapshot(id);
    }
    return { snapshot: null };
  }
  if (AppVersion === 'WEB') {
    return webSnapshotApi(modelId, `/${encodeURIComponent(id)}`);
  }
  try {
    const res = await fetch(`https://process-optimizer-jira.metricus.com/api/processmodel/${modelId}/consultant-snapshot/${id}`);
    return await res.json();
  } catch (err) { console.error('getSnapshot [Direct] failed:', err); return { snapshot: null }; }
};

export const deleteSnapshot = async ({ id, modelId, AppVersion }) => {
  if (isJiraFreeEdition()) return { success: false, error: 'Snapshots require Enterprise' };
  if (AppVersion === 'FORGE') return invoke('deleteConsultantSnapshot', { id });
  if (AppVersion === 'SNOW') {
    if (_snApi && typeof _snApi.deleteSnapshot === 'function') {
      return _snApi.deleteSnapshot(id);
    }
    return { success: false };
  }
  if (AppVersion === 'WEB') {
    return webSnapshotApi(modelId, `/${encodeURIComponent(id)}`, {method:'DELETE'});
  }
  try {
    const res = await fetch(`https://process-optimizer-jira.metricus.com/api/processmodel/${modelId}/consultant-snapshot/${id}`, { method: 'DELETE' });
    return await res.json();
  } catch (err) { console.error('deleteSnapshot [Direct] failed:', err); return { success: false }; }
};

export const updateSnapshotDescription = async ({ id, modelId, description, AppVersion }) => {
  if (AppVersion === 'FORGE') return invoke('updateConsultantSnapshotDescription', { id, description });
  if (AppVersion === 'SNOW') {
    if (_snApi && typeof _snApi.updateSnapshotDescription === 'function') {
      return _snApi.updateSnapshotDescription(id, description);
    }
    return { success: false };
  }
  if (AppVersion === 'WEB') {
    return webSnapshotApi(modelId, `/${encodeURIComponent(id)}`, {method:'PATCH',body:JSON.stringify({description})});
  }
  try {
    const res = await fetch(`https://process-optimizer-jira.metricus.com/api/processmodel/${modelId}/consultant-snapshot/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description }),
    });
    return await res.json();
  } catch (err) { console.error('updateSnapshotDescription [Direct] failed:', err); return { success: false }; }
};
